import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Users } from 'lucide-react';
import { colors } from '../../constants/theme';
import { competitionService } from '../../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

/**
 * Tabela de classificação dos grupos de uma categoria.
 *
 * `refreshIntervalMs` liga a atualização automática (usada no painel do
 * organizador durante o evento). O recarregamento em segundo plano não mostra
 * o spinner — trocar a tabela por "Carregando..." a cada ciclo tornaria a tela
 * inútil para acompanhar as partidas.
 */
const GroupTable = ({ category, refreshIntervalMs = 0, refreshSignal = 0, onRefreshed }) => {
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Categoria pedida no render atual, para descartar respostas que chegam
  // depois de o organizador já ter trocado de aba.
  const currentCategory = useRef(category);
  currentCategory.current = category;

  useEffect(() => {
    loadGroups();
  }, [category]);

  useEffect(() => {
    if (!refreshIntervalMs) return;
    const interval = setInterval(() => loadGroups({ background: true }), refreshIntervalMs);
    return () => clearInterval(interval);
  }, [refreshIntervalMs, category]);

  // Atualização sob demanda, sem remontar o componente (não pisca a tabela).
  useEffect(() => {
    if (refreshSignal) loadGroups({ background: true });
  }, [refreshSignal]);

  const loadGroups = async ({ background = false } = {}) => {
    const requested = category;
    // Se o organizador trocou de categoria enquanto a resposta vinha, esta
    // requisição está obsoleta e não pode mexer em nenhum estado — inclusive
    // no loading, que já pertence à requisição da nova categoria.
    const isStale = () => currentCategory.current !== requested;

    try {
      if (!background) setLoading(true);
      setError(null);

      const response = await competitionService.getGroups();
      if (isStale()) return;

      if (response.success && response.data.groups && response.data.groups[requested]) {
        setGroups(response.data.groups[requested]);
      } else {
        // Competição existe, mas esta categoria ainda não tem grupos sorteados
        setGroups({});
      }

      if (onRefreshed) onRefreshed(new Date());
    } catch (error) {
      if (isStale()) return;
      console.error('❌ Erro ao carregar grupos:', error);
      // 404 = nenhuma competição com grupos sorteados ainda (não é um erro de fato)
      if (error.status === 404) {
        setGroups({});
      } else {
        setError('Erro ao carregar grupos: ' + (error.error?.message || error.message || 'Erro desconhecido'));
      }
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Carregando grupos..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadGroups} />;
  }

  const groupNames = Object.keys(groups);

  if (groupNames.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-700 font-medium mb-2">Grupos ainda não sorteados!</p>
        <p className="text-sm text-gray-400">
          Os grupos da categoria {category} aparecerão aqui após o sorteio.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupNames.map((groupName) => {
        const players = Array.isArray(groups[groupName]) ? groups[groupName] : [];
        const hasStats = players.length > 0 && players.some(p => p.stats && p.stats.matches > 0);
        
        return (
          <div key={groupName} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="text-white p-4 text-center font-bold" style={{ backgroundColor: colors.primary }}>
              {groupName.toUpperCase()} - CATEGORIA {category.toUpperCase()}
            </div>
            
            {!hasStats && (
              <div className="bg-blue-50 border-b border-blue-200 p-3">
                <p className="text-sm text-blue-700 text-center">
                  ℹ️ Grupo sorteado! Estatísticas serão atualizadas conforme as partidas forem finalizadas.
                </p>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Pos</th>
                    <th className="px-4 py-3 text-left">Jogador</th>
                    <th className="px-4 py-3 text-center">P</th>
                    <th className="px-4 py-3 text-center">V</th>
                    <th className="px-4 py-3 text-center">E</th>
                    <th className="px-4 py-3 text-center">D</th>
                    <th className="px-4 py-3 text-center">GF</th>
                    <th className="px-4 py-3 text-center">GS</th>
                    <th className="px-4 py-3 text-center">SG</th>
                    <th className="px-4 py-3 text-center">Pts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {players.map((player, index) => {
                    const position = index + 1;
                    // O backend calcula quantos este grupo classifica (um grupo
                    // de 5 classifica 3), então não dá para assumir top 2 aqui.
                    const qualified = player.qualified && hasStats;
                    // 3º lugar pegando vaga de repescagem NESTE MOMENTO —
                    // muda a cada partida encerrada em qualquer grupo.
                    const inRepechage = player.repechage && hasStats;
                    const tiedInRepechage = player.repechageTied && hasStats;

                    // Quem empatou mas está fora da vaga também aparece
                    // destacado: ele ainda está na disputa.
                    const rowClass = qualified
                      ? 'bg-green-50'
                      : inRepechage || tiedInRepechage
                      ? 'bg-amber-50'
                      : '';

                    return (
                      <tr key={player.id} className={rowClass}>
                        <td className="px-4 py-3 font-bold">
                          {position}
                          {qualified && <span className="text-green-600 ml-1">✓</span>}
                          {inRepechage && <span className="text-amber-600 ml-1">✓?</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{player.name || 'Sem nome'}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {player.stats?.matches || 0}
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-green-600">
                          {player.stats?.wins || 0}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">
                          {player.stats?.draws || 0}
                        </td>
                        <td className="px-4 py-3 text-center text-red-600">
                          {player.stats?.losses || 0}
                        </td>
                        <td className="px-4 py-3 text-center text-green-600">
                          {player.stats?.goalsFor || 0}
                        </td>
                        <td className="px-4 py-3 text-center text-red-600">
                          {player.stats?.goalsAgainst || 0}
                        </td>
                        <td className="px-4 py-3 text-center font-bold">
                          <span className={(player.stats?.goalDifference || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {(player.stats?.goalDifference || 0) > 0 ? '+' : ''}
                            {player.stats?.goalDifference || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-lg">
                          {player.stats?.points || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="p-3 bg-gray-50 text-sm text-gray-600">
              <div className="flex justify-between items-center">
                <span className="text-xs">
                  P=Partidas | V=Vitórias | E=Empates | D=Derrotas | GF/GS=Gols | SG=Saldo | Pts=Pontos
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GroupTable;